import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Spinner, Badge } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import {
  IoIosNotifications,
  IoIosCheckmarkCircle,
  IoIosInformationCircle,
  IoIosWarning,
  IoIosCloseCircle,
  IoIosClose,
  IoIosTrash,
  IoIosRefresh,
} from 'react-icons/io';
import { FiCheck, FiCheckCircle, FiBell, FiBellOff } from 'react-icons/fi';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import notificationAPI from '../services/notificationAPI';
import {
  setUnreadCount,
  setNotifications,
  appendNotifications,
  addNotification,
  markNotificationAsRead,
  markAllAsRead,
  removeNotification,
  setLoading,
} from '../application/reducers/notification';
import { useSocketEvent } from '../contexts/SocketContext';
import './NotificationPanel.scss';

// ─── Time helpers ──────────────────────────────
const timeAgo = (timestamp) => {
  const now = new Date();
  const date = new Date(timestamp);
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getSectionLabel = (timestamp) => {
  const now = new Date();
  const d = new Date(timestamp);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const notifDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = Math.floor((today - notifDay) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff <= 6) return 'This Week';
  return 'Earlier';
};

// ─── Category → icon / color ──────────────────
const CATEGORY_CONFIG = {
  info:    { Icon: IoIosInformationCircle, color: '#3b82f6', bg: '#eff6ff' },
  success: { Icon: IoIosCheckmarkCircle,   color: '#10b981', bg: '#ecfdf5' },
  warning: { Icon: IoIosWarning,           color: '#f59e0b', bg: '#fffbeb' },
  error:   { Icon: IoIosCloseCircle,       color: '#ef4444', bg: '#fef2f2' },
};

const getCategory = (n) => CATEGORY_CONFIG[n?.category] || CATEGORY_CONFIG.info;

/**
 * NotificationPanel — Navbar notification bell + animated dropdown.
 */
const NotificationPanel = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((s) => s.auth);
  const { unreadCount, notifications, loading } = useSelector((s) => s.notifications);
  const userId = user?.user_id;

  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'
  const panelRef = useRef(null);
  const listRef = useRef(null);

  // ─── Fetch unread count on mount ─────────────
  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const res = await notificationAPI.getUnreadCount(userId);
        if (res?.data) dispatch(setUnreadCount(res.data.unread_count || 0));
      } catch {
        /* silent */
      }
    })();
  }, [userId, dispatch]);

  // ─── Polling every 60 s ──────────────────────
  useEffect(() => {
    if (!userId) return;
    const id = setInterval(async () => {
      try {
        const res = await notificationAPI.getUnreadCount(userId);
        if (res?.data) dispatch(setUnreadCount(res.data.unread_count || 0));
      } catch {
        /* silent */
      }
    }, 300_000);
    return () => clearInterval(id);
  }, [userId, dispatch]);

  // ─── Real-time via existing socket ───────────
  useSocketEvent('new_notification', (notification) => {
    if (notification) dispatch(addNotification(notification));
  });

  // ─── Fetch notifications page ────────────────
  const fetchPage = useCallback(
    async (pg = 1, isRefresh = false) => {
      if (!userId) return;
      try {
        if (pg === 1 && !isRefresh) dispatch(setLoading(true));
        if (pg > 1) setLoadingMore(true);

        const res = await notificationAPI.getUserNotifications(userId, {
          page: pg,
          limit: 20,
          unread_only: filter === 'unread',
        });

        const items = res?.data?.notifications || [];
        if (pg === 1) {
          dispatch(setNotifications(items));
        } else {
          dispatch(appendNotifications(items));
        }
        if (res?.data?.unread_count !== undefined) {
          dispatch(setUnreadCount(res.data.unread_count));
        }
        setHasMore(items.length === 20);
        setPage(pg);
      } catch {
        /* silent */
      } finally {
        dispatch(setLoading(false));
        setLoadingMore(false);
      }
    },
    [userId, filter, dispatch],
  );

  // ─── Load on open / filter change ────────────
  useEffect(() => {
    if (open) fetchPage(1);
  }, [open, filter, fetchPage]);

  // ─── Close on outside click ──────────────────
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // ─── Scroll to load more ────────────────────
  const handleScroll = () => {
    const el = listRef.current;
    if (!el || loadingMore || !hasMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      fetchPage(page + 1);
    }
  };

  const navigate = useNavigate();

  // ─── Navigate on notification click ─────────
  const handleNotificationClick = async (n) => {
    // Mark as read silently
    if (!n.isRead) {
      try {
        await notificationAPI.markAsRead(n._id);
        dispatch(markNotificationAsRead(n._id));
      } catch { /* silent */ }
    }

    // Close the panel
    setOpen(false);

    // Route based on notification type / module
    const type = n.notification_type || n.type || '';
    const module = n.data?.module || '';

    if (module === 'fee_management' || type.startsWith('fee_')) {
      if (type === 'fee_payment_recorded' || type === 'fee_payment_verified' || type === 'fee_payment_refunded') {
        navigate('/student-fees/payments');
      } else if (type === 'fee_structure_applied') {
        navigate('/student-fees/structures');
      } else {
        navigate('/student-fees/student-fees');
      }
      return;
    }

    if (module === 'feedback') {
      navigate('/feedback/manage');
      return;
    }

    if (module === 'store_management') {
      const type = n.notification_type || n.type || '';
      if (type === 'store_low_stock') navigate('/store-management/alerts');
      else if (type === 'store_pr_submitted' || type === 'store_pr_actioned') navigate('/store-management/requisitions');
      else if (type === 'store_ir_submitted' || type === 'store_ir_actioned') navigate('/store-management/issues');
      else navigate('/store-management/dashboard');
      return;
    }
  };

  // ─── Actions ────────────────────────────────
  const handleMarkRead = async (e, n) => {
    e.stopPropagation();
    if (n.isRead) return;
    try {
      await notificationAPI.markAsRead(n._id);
      dispatch(markNotificationAsRead(n._id));
    } catch {
      /* silent */
    }
  };

  const handleMarkAllRead = async () => {
    if (!userId) return;
    try {
      await notificationAPI.markAllAsRead(userId);
      dispatch(markAllAsRead());
    } catch {
      /* silent */
    }
  };

  const handleDelete = async (e, n) => {
    e.stopPropagation();
    try {
      await notificationAPI.deleteNotification(n._id);
      dispatch(removeNotification(n._id));
    } catch {
      /* silent */
    }
  };

  // ─── Grouped by section ─────────────────────
  const grouped = notifications.reduce((acc, n) => {
    const label = getSectionLabel(n.createdAt || n.timestamp);
    if (!acc[label]) acc[label] = [];
    acc[label].push(n);
    return acc;
  }, {});
  const sectionOrder = ['Today', 'Yesterday', 'This Week', 'Earlier'];
  const sections = sectionOrder.filter((s) => grouped[s]?.length);

  return (
    <div className="notification-panel-wrapper" ref={panelRef}>
      {/* ─── Bell trigger ──────────────────── */}
      <motion.button
        className="np-bell-btn"
        onClick={() => setOpen((v) => !v)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.92 }}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
      >
        <IoIosNotifications size={23} color="#fff" />
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              className="np-badge"
              key="badge"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 600, damping: 25 }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* ─── Dropdown panel ────────────────── */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="np-panel"
            initial={{ opacity: 0, y: -12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.96 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            {/* Header */}
            <div className="np-header">
              <h6 className="np-title">
                <FiBell className="me-2" size={16} />
                Notifications
                {unreadCount > 0 && (
                  <Badge bg="danger" pill className="ms-2 np-count-badge">
                    {unreadCount}
                  </Badge>
                )}
              </h6>
              <div className="np-header-actions">
                {unreadCount > 0 && (
                  <button className="np-action-btn" onClick={handleMarkAllRead} title="Mark all read">
                    <FiCheckCircle size={15} />
                  </button>
                )}
                <button className="np-action-btn" onClick={() => fetchPage(1, true)} title="Refresh">
                  <IoIosRefresh size={16} />
                </button>
                <button className="np-action-btn np-close-btn" onClick={() => setOpen(false)} title="Close">
                  <IoIosClose size={20} />
                </button>
              </div>
            </div>

            {/* Filter tabs */}
            <div className="np-tabs">
              <button
                className={`np-tab ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                All
              </button>
              <button
                className={`np-tab ${filter === 'unread' ? 'active' : ''}`}
                onClick={() => setFilter('unread')}
              >
                Unread {unreadCount > 0 && `(${unreadCount})`}
              </button>
            </div>

            {/* List */}
            <div className="np-list" ref={listRef} onScroll={handleScroll}>
              {loading && notifications.length === 0 ? (
                <div className="np-empty">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading notifications...
                </div>
              ) : notifications.length === 0 ? (
                <div className="np-empty">
                  <FiBellOff size={36} className="np-empty-icon" />
                  <span>No {filter === 'unread' ? 'unread ' : ''}notifications</span>
                  <small>You're all caught up!</small>
                </div>
              ) : (
                sections.map((section) => (
                  <div key={section} className="np-section">
                    <div className="np-section-label">{section}</div>
                    {grouped[section].map((n, i) => {
                      const cat = getCategory(n);
                      return (
                        <motion.div
                          key={n._id}
                          className={`np-item ${n.isRead ? '' : 'unread'}`}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.03, duration: 0.2 }}
                          onClick={() => handleNotificationClick(n)}
                        >
                          <div className="np-item-icon" style={{ background: cat.bg }}>
                            <cat.Icon size={20} color={cat.color} />
                          </div>
                          <div className="np-item-body">
                            <div className="np-item-title">{n.title || 'Notification'}</div>
                            <div className="np-item-message">{n.message || n.body || ''}</div>
                            <div className="np-item-time">{timeAgo(n.createdAt || n.timestamp)}</div>
                          </div>
                          <div className="np-item-actions">
                            {!n.isRead && (
                              <button
                                className="np-item-action"
                                title="Mark as read"
                                onClick={(e) => handleMarkRead(e, n)}
                              >
                                <FiCheck size={14} />
                              </button>
                            )}
                            <button
                              className="np-item-action np-item-delete"
                              title="Delete"
                              onClick={(e) => handleDelete(e, n)}
                            >
                              <IoIosTrash size={15} />
                            </button>
                          </div>
                          {!n.isRead && <span className="np-unread-dot" />}
                        </motion.div>
                      );
                    })}
                  </div>
                ))
              )}

              {loadingMore && (
                <div className="np-loading-more">
                  <Spinner animation="border" size="sm" />
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationPanel;
